import type { FormEvent } from "react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/atoms";
import { AuthHeader, LoginForm } from "@/components/auth";
import { routes, strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import { useLogin } from "@/hooks";
import { AuthLayout } from "@/layouts";
import { validateLoginFormHandler } from "@/utils";

export const LoginPage = () => {
    const [email, setEmail] = useState("");

    const [password, setPassword] = useState("");

    const [validationError, setValidationError] = useState("");

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const {
        error,
        isLoading,
        onReset,
        onRun: runLoginHandler,
    } = useLogin();

    const returnUrl = searchParams.get("returnUrl") || routes.benchmark;

    const displayError = validationError || error;

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setValidationError("");

        onReset();

        const validation = validateLoginFormHandler(
            email,
            password,
        );

        if (!validation.isValid) {
            setValidationError(validation.error || strings.errors.checkInput);

            return;
        }

        try {
            await runLoginHandler({
                email,
                password,
            });

            navigate(returnUrl);
        } catch (loginErr) {
            console.error(
                strings.debug.loginFailed,
                loginErr,
            );
        }
    };

    const dismissErrorHandler = () => {
        setValidationError("");

        onReset();
    };

    return (
        <AuthLayout>
            <div>
                <AuthHeader
                    description={strings.login.description}
                    title={strings.login.title}
                />
                {displayError && (
                    <Alert
                        className="mb-6"
                        variant={AlertVariantEnum.ERROR}
                        onClose={dismissErrorHandler}
                    >
                        {displayError}
                    </Alert>
                )}
                <LoginForm
                    email={email}
                    error={displayError || ""}
                    isLoading={isLoading}
                    password={password}
                    onForgotPassword={() => navigate(routes.forgotPassword)}
                    onSubmit={submitHandler}
                    onChangeEmail={(value) => {
                        setEmail(value);

                        setValidationError("");
                    }}
                    onChangePassword={(value) => {
                        setPassword(value);

                        setValidationError("");
                    }}
                />
            </div>
        </AuthLayout>
    );
};
